import { MenuProps, Spin } from 'antd';
import { LocationTableFields } from 'enums/CompanySetupEnums';
import { DownloadKeys, DownloadLoader } from 'enums/GraphDropdownEnums';
import React, { FC, useMemo, useRef, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Label,
  Tooltip,
  TooltipProps,
} from 'recharts';
import SbtiService from 'services/Sbti/sbti.service';
import CustomTooltip from 'shared/components/CustomisedTooltip';
import { downloadGraph } from 'shared/utils/downloadStaticGraph';
import { formatValue } from 'shared/utils/formatWholeNum';
import GraphDropDown from 'views/Dashboard/COBarChart/GraphDropdown';
import {
  CompanyDashboardProps,
  customFontTick,
  customMarginLineChart,
  customPosition,
  customPositionX,
  customStyleAnchor,
  Linear,
  MyLineChartProps,
} from '../constants';
import { CustomTick } from './customTick';
import styles from './linechart.module.scss';
import { xAxisLabelContent } from './xAxisLabelContent';
import { formatAxisTickValue, getNiceYAxisScale } from 'shared/utils/chartScale';

function toFiniteNumber(val: unknown): number | undefined {
  if (typeof val === 'number' && Number.isFinite(val)) return val;
  if (typeof val === 'string') {
    const n = parseFloat(val);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

function getTotalSeriesY(row: Record<string, unknown>): number | undefined {
  return (
    toFiniteNumber(row.totalCO2e) ?? toFiniteNumber(row.total_co2e)
  );
}

function getTargetSeriesY(row: Record<string, unknown>): number | undefined {
  return (
    toFiniteNumber(row.targetCO2e) ??
    toFiniteNumber(row.target_co2e)
  );
}

const MyLineChart: FC<MyLineChartProps> = ({
  data,
  isTwoLine,
  xPos,
  orgId,
  maxSbtiValue: _maxSbtiValue,
  targetName,
  targetId,
  selectedYear,
  fetchYoYEmissions,
  isProdDashboard,
  locationId,
  height,
  AxisProp,
  unit,
  hasDownload = true,
  loading,
  yAxisLabel,
}) => {
  const graphRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const { downloadSbtiCsv } = SbtiService();
  const handleDownload: MenuProps['onClick'] = async ({ key }) => {
    await downloadGraph(
      key,
      graphRef,
      CompanyDashboardProps.YOYCHART,
      setIsDownloading
    );
    if (key === DownloadKeys.CSV) {
      if (targetId) {
        downloadSbtiCsv(targetId);
      } else {
        if (
          locationId !== CompanyDashboardProps.ALL &&
          locationId !== undefined
        ) {
          fetchYoYEmissions({
            organizationId: orgId,
            locationId,
            year: selectedYear,
            targetName:
              targetName?.trim() === CompanyDashboardProps.SELECT_TARGET
                ? undefined
                : targetName,

            exportType: DownloadKeys.CSV,
          });
        } else {
          isProdDashboard
            ? fetchYoYEmissions({
                productId: orgId,
                exportType: DownloadKeys.CSV,
              })
            : fetchYoYEmissions({
                organizationId: orgId,
                year: selectedYear,
                targetName:
                  targetName?.trim() === CompanyDashboardProps.SELECT_TARGET
                    ? undefined
                    : targetName,
                exportType: DownloadKeys.CSV,
              });
        }
      }
    }
  };

  const handleLineSort: MenuProps['onClick'] = ({ key }) => {
    const order =
      key === CompanyDashboardProps.TRUE
        ? LocationTableFields.ASC
        : LocationTableFields.DESC;
    fetchYoYEmissions(orgId, order);
  };
  const customPadding = {
    left: data?.length === 1 ? 90 : 10,
    right: data?.length === 1 ? 400 : 0,
  };

  /** Shared scale: fixed step sizes from 0 so Y ticks are evenly spaced (matches bar-chart pattern). */
  const yAxisScale = useMemo(() => {
    const samples: number[] = [];
    data?.forEach((entry) => {
      const row = entry as unknown as Record<string, unknown>;
      const totalY = getTotalSeriesY(row);
      if (totalY !== undefined) samples.push(totalY);
      const targetY = getTargetSeriesY(row);
      if (targetY !== undefined) samples.push(targetY);
    });
    return getNiceYAxisScale(samples, {
      tickCount: 4,
      anchorAtZero: true,
    });
  }, [data]);
  const isPercentageZero = data?.length !== 0;
  const formatTick = useMemo(
    () => (value: number) => formatAxisTickValue(value, yAxisScale.ticks),
    [yAxisScale.ticks]
  );
  const renderTooltipContent = (tooltipProps: TooltipProps<string, string>) => {
    const { payload } = tooltipProps;
    return payload && payload.length ? (
      <CustomTooltip
        targetName={targetName}
        unit={unit}
        payload={payload[0]?.payload}
      />
    ) : null;
  };
  return (
    <>
      {isPercentageZero ? (
        <Spin
          spinning={isDownloading || !!loading}
          {...(isDownloading && { tip: DownloadLoader.DOWNLOADING })}>
          <div className={styles.wrapper}>
            <div className={styles.positionedDiv}>
              <GraphDropDown
                handleDownload={handleDownload}
                showSort={false}
                isDownload={hasDownload}
                isMainDashboard
                handleSort={handleLineSort}
                graphRef={graphRef}
                setIsDownloading={setIsDownloading}
              />
            </div>
            <div ref={graphRef}>
              <ResponsiveContainer width="100%" height={height ? height : 350}>
                <LineChart data={data} margin={customMarginLineChart}>
                  <CartesianGrid
                    strokeDasharray="5 5"
                    horizontal={true}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="year"
                    angle={-45}
                    dy={10}
                    dx={-10}
                    axisLine={false}
                    tickLine={false}
                    tick={
                      <CustomTick
                        x={20}
                        y={0}
                        payload={{
                          value: '',
                        }}
                      />
                    }
                    padding={customPadding}>
                    {isTwoLine ? (
                      <Label
                        content={
                          xPos
                            ? xAxisLabelContent(
                                xPos,
                                targetName !==
                                  CompanyDashboardProps.SELECT_TARGET
                                  ? targetName
                                  : undefined
                              )
                            : xAxisLabelContent(
                                undefined,
                                targetName !==
                                  CompanyDashboardProps.SELECT_TARGET
                                  ? targetName
                                  : undefined
                              )
                        }
                        position={customPositionX}
                        offset={-44}
                        style={customStyleAnchor}
                      />
                    ) : (
                      <Label
                        value={AxisProp}
                        position={customPositionX}
                        offset={-24}
                        style={customStyleAnchor}
                      />
                    )}
                  </XAxis>

                  <YAxis
                    domain={yAxisScale.domain}
                    ticks={yAxisScale.ticks}
                    scale="linear"
                    tick={customFontTick}
                    axisLine={false}
                    tickFormatter={formatTick}
                    tickLine={false}
                  >
                    <Label
                      value={yAxisLabel ?? CompanyDashboardProps.LABEL_VALUE + `(${unit})`}
                      position={customPosition}
                      angle={-90}
                      offset={-10}
                      style={customStyleAnchor}
                    />
                  </YAxis>

                  {/* Line for the sbtiDetails yoyEmission data */}
                  {isTwoLine ? (
                    <>
                      <Line
                        type={Linear}
                        dataKey={(row) =>
                          getTotalSeriesY(row as Record<string, unknown>) ?? 0
                        }
                        stroke={CompanyDashboardProps.LINE_STROKE}
                        strokeWidth={2}
                        dot={{ r: 2 }}
                        activeDot={{ r: 5 }}
                      />
                      <Line
                        type={Linear}
                        dataKey={(row) =>
                          getTargetSeriesY(row as Record<string, unknown>) ?? 0
                        }
                        stroke={CompanyDashboardProps.TARGET_STROKE}
                        strokeWidth={2}
                        dot={{ r: 2 }}
                        activeDot={{ r: 5 }}
                      />
                    </>
                  ) : (
                    <Line
                      type={Linear}
                      dataKey={(row) =>
                        getTotalSeriesY(row as Record<string, unknown>) ?? 0
                      }
                      stroke={CompanyDashboardProps.LINE_STROKE}
                      strokeWidth={CompanyDashboardProps.LINE_STROKE_WIDTH}
                      dot={{ r: 2 }}
                      activeDot={{ r: 5 }}
                      label={({ x, y, value }) => (
                        <text
                          x={x}
                          y={y}
                          dy={-16}
                          dx={3}
                          fill={CompanyDashboardProps.LINE_FILL}
                          fontSize={12}
                          style={customStyleAnchor}>
                          {formatValue(value)}
                        </text>
                      )}
                    />
                  )}

                  <Tooltip cursor={false} content={renderTooltipContent} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Spin>
      ) : (
        <p className={styles.emptyText}>
          {isProdDashboard
            ? CompanyDashboardProps.NO_DATA
            : CompanyDashboardProps.DATA_EMPTY}
        </p>
      )}
    </>
  );
};

export default MyLineChart;
